const WebSocketService = require('./WebSocketService');
const { getIframeLoader } = require('../util');
const { elements } = require('../variables');
const { mainConfigService } = require('../services/config');
const { CSS_CLASSES } = require('../constants');

/**
 * 3DS Service for handling 3DS authentication popup
 * Extracted from Selector.generate3DSPopUp for reusability
 */
class ThreeDSService {
  constructor(documentCheckout, callbackService, fieldsService) {
    this.fieldsService = fieldsService;
    this.webSocketService = new WebSocketService(this.fieldsService);
    this.documentCheckout = documentCheckout;
    this.callbackService = callbackService;
    this.threeDSContainer = null;
    this.iframeElement = null;
  }

  /**
   * Set the document checkout element
   * @param {HTMLElement} documentCheckout - The checkout document element
   */
  setDocumentCheckout(documentCheckout) {
    this.documentCheckout = documentCheckout;
  }

  /**
   * Generate 3DS popup and handle authentication
   * @param {Object} response - 3DS response object
   * @param {string} response.ws_url - WebSocket URL
   * @param {string} response.reference_number - Reference number
   * @param {string} response.html - 3DS HTML content
   */
  async generate3DSPopUp(response) {
    if (!this.validateElements()) {
      throw new Error('Required DOM elements not found');
    }

    this.setup3DSPopup(response);

    await this.connectWebSocket(response);
  }

  /**
   * Validate that required DOM elements exist
   * @returns {boolean} - Returns true if all elements exist
   */
  validateElements() {
    if (!this.documentCheckout) {
      console.error('Document checkout element not set');
      return false;
    }

    this.sdkElement = this.documentCheckout.querySelector(
      '#' + elements.sdkChild
    );
    this.threeDSContainer = this.documentCheckout.querySelector(
      '#' + elements.threeDS
    );

    if (!this.sdkElement || !this.threeDSContainer) {
      console.error('Required 3DS elements not found');
      return false;
    }

    return true;
  }

  /**
   * Setup 3DS popup HTML and elements using Blob URL iframe
   * @param {Object} response - 3DS response object
   */
  setup3DSPopup(response) {
    this.show3DSPopup();

    this.threeDSContainer.innerHTML = getIframeLoader();

    const iframe = document.createElement('iframe');
    iframe.id = 'dsIframe';
    iframe.setAttribute('data-testid', 'ottu-sdk-3ds-iframe');
    iframe.className = 'ottu__sdk-ds-block 3ds-container';
    iframe.style.cssText = 'width: 100%; max-width: 100%; height: 850px; border: none; display: block; overflow: hidden;';

    iframe.sandbox = 'allow-scripts allow-forms allow-top-navigation allow-same-origin';

    // Inject CSS to ensure all nested iframes have no border
    const iframeStyles = `<style>
      iframe, iframe[name="challengeFrame"], iframe[id*="challengeFrame"] {
        border: none;
        outline: none;
        display: flex;
        margin-inline: auto;
      }
    </style>`;

    let modifiedHtml = response.html;
    if (modifiedHtml.includes('<head>')) {
      modifiedHtml = modifiedHtml.replace('<head>', '<head>' + iframeStyles);
    } else if (modifiedHtml.includes('<html>')) {
      modifiedHtml = modifiedHtml.replace('<html>', '<html>' + iframeStyles);
    } else {
      // Fallback: prepend styles to the HTML
      modifiedHtml = iframeStyles + modifiedHtml;
    }

    const blob = new Blob([modifiedHtml], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    iframe.src = blobUrl;

    let loadCount = 0;
    iframe.onload = () => {
      if (loadCount === 0) {
        URL.revokeObjectURL(blobUrl);
        this.showIframe();
      }
      loadCount++;

      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const challengeFrame = iframeDoc.querySelector('iframe[name="challengeFrame"], iframe[id*="challengeFrame"]');
        if (challengeFrame) {
          const heightAttr = challengeFrame.getAttribute('height');
          if (heightAttr) {
            const height = parseInt(heightAttr, 10);
            if (!isNaN(height)) {
              iframe.style.height = (height + 50) + 'px';
            }
          }
        }
      } catch (e) {
        // Cross-origin access may fail silently
      }
    };

    this.threeDSContainer.appendChild(iframe);

    this.iframeElement = iframe;
  }

  /**
   * Show 3DS popup
   */
  show3DSPopup() {
    if (this.sdkElement && this.threeDSContainer) {
      this.sdkElement.style.display = 'none';
      this.threeDSContainer.style.display = 'block';
    }
  }

  /**
   * Hide 3DS popup
   */
  hide3DSPopup() {
    if (this.sdkElement && this.threeDSContainer) {
      this.sdkElement.style.display = 'block';
      this.threeDSContainer.style.display = 'none';
    }
  }

  /**
   * Show iframe content and hide loader
   */
  showIframe() {
    const iframeLoader = this.documentCheckout.querySelector(
      '#' + elements.blockIframeLoader
    );

    if (iframeLoader) {
      iframeLoader.classList.add('hide');
    }
  }

  /**
   * Connect to WebSocket for 3DS authentication
   * @param {Object} response - 3DS response object
   * @param {Object} callbacks - Callback functions
   */
  async connectWebSocket(response) {
    const wsCallbacks = this.getWebSocketCallbacks();
    await this.webSocketService.connect({
      wsUrl: response.ws_url,
      referenceNumber: response.reference_number,
      merchantId: mainConfigService.get('merchant_id'),
      callbacks: wsCallbacks,
    });
  }

  /**
   * Close 3DS popup and cleanup
   */
  close() {
    this.hide3DSPopup();
    this.webSocketService.close();
  }

  /**
   * Get WebSocket service instance
   * @returns {WebSocketService} - Returns WebSocket service instance
   */
  getWebSocketService() {
    return this.webSocketService;
  }

  /**
   * Get pay button element from DOM
   * @returns {HTMLElement|null} - Returns pay button element or null
   */
  _getPayButton() {
    return this.documentCheckout.querySelector('#' + elements.payButton);
  }

  /**
   * Re-enable pay button for failed or cancelled transactions
   */
  _handleFailedCancelledTransaction() {
    const payButton = this._getPayButton();
    if (payButton) {
      payButton.classList.remove(CSS_CLASSES.DISABLED_PAY_BUTTON);
    }
  }

  /**
   * Get WebSocket callbacks for 3DS service
   * @returns {Object} - Object containing WebSocket callback functions
   */
  getWebSocketCallbacks() {
    return {
      onSuccess: (res) => {
        this.callbackService.callBackSuccess(res);
      },
      onCancel: (res) => {
        this.callbackService.callBackCancel(res);
        this.hide3DSPopup();
        this._handleFailedCancelledTransaction(res);
      },
      onError: (res) => {
        this.callbackService.callBackError(res);
        this.hide3DSPopup();
        this._handleFailedCancelledTransaction(res);
      },
      onClose: () => {
        console.log('WebSocket Connection Closed');
      },
    };
  }
}

module.exports = ThreeDSService;
